import React, {useEffect,useRef,useState} from 'react';
import {Camera,Check,ImagePlus,Loader2,Sparkles,X} from 'lucide-react';
import {applyVisualProductSuggestion,validateProductPhoto,detectBarcodeFromImage,isValidGtin} from './domain.js';
import {findExistingProducts,proposeClassification} from './productMaster.js';

async function preparePhoto(file){
  const check=validateProductPhoto(file);
  if(!check.valid)throw new Error(check.error);
  if(!['image/jpeg','image/png','image/webp'].includes(file.type)||
     typeof createImageBitmap!=='function')return file;
  let bitmap;
  try{
    bitmap=await createImageBitmap(file);
    const scale=Math.min(1,1600/Math.max(bitmap.width,bitmap.height));
    const canvas=document.createElement('canvas');
    canvas.width=Math.max(1,Math.round(bitmap.width*scale));
    canvas.height=Math.max(1,Math.round(bitmap.height*scale));
    canvas.getContext('2d',{alpha:false}).drawImage(bitmap,0,0,canvas.width,canvas.height);
    const blob=await new Promise(resolve=>canvas.toBlob(resolve,'image/webp',0.84));
    if(!blob)return file;
    return new File([blob],'produto-'+Date.now()+'.webp',{type:'image/webp'});
  }catch{return file}
  finally{bitmap?.close?.()}
}

function CommerceInput({label,value,onChange,multiline=false,hint}){
  return <label className="commerceInput"><strong>{label}</strong>
    {multiline?<textarea value={value||''} onChange={e=>onChange(e.target.value)}/>:
      <input value={value||''} onChange={e=>onChange(e.target.value)}/>}
    {hint&&<small>{hint}</small>}
  </label>;
}

export default function CatalogEnhancement({
  form,setForm,photoFile,photoPreview,extraPhotos,setExtraPhotos,
  onIdentify,categories=[],products=[],onOpenExisting,mode='media'
}){
  const [photoError,setPhotoError]=useState('');
  const [aiError,setAiError]=useState('');
  const [aiBusy,setAiBusy]=useState(false);
  const [aiResult,setAiResult]=useState(null);
  const [barcodeNotice,setBarcodeNotice]=useState('');
  const [barcodeBusy,setBarcodeBusy]=useState(false);
  const processedPhotos=useRef(new WeakSet());
  const detectedMatches=findExistingProducts(products,form,form.id);
  useEffect(()=>{
    const files=[photoFile,...extraPhotos.map(i=>i.file)].filter(Boolean);
    const recent=files.filter(file=>!processedPhotos.current.has(file));
    if(!recent.length)return;
    let alive=true;
    (async()=>{
      setBarcodeBusy(true);
      for(const file of recent){
        processedPhotos.current.add(file);
        let code;
        try{code=await detectBarcodeFromImage(file)}catch{code=null}
        if(!alive)return;
        if(code&&isValidGtin(code)){
          setForm(current=>current.barcode?current:{...current,barcode:code});
          setBarcodeNotice('Código GTIN encontrado na foto: '+code+'. Confira o rótulo.');
          break;
        }
      }
      if(alive)setBarcodeBusy(false);
    })();
    return()=>{alive=false};
  },[photoFile,extraPhotos,setForm]);
  
  const commerce=form.metadata?.commerce||{};
  const setCommerce=(key,value)=>setForm(current=>({...current,
    metadata:{...(current.metadata||{}),commerce:{...(current.metadata?.commerce||{}),[key]:value}}
  }));
  const addPhotos=async files=>{
    const slots=Math.max(0,2-extraPhotos.length);
    if(!slots||!files?.length)return;
    setPhotoError('');
    try{
      const prepared=await Promise.all([...files].slice(0,slots).map(preparePhoto));
      setExtraPhotos(current=>[
        ...current,
        ...prepared.map(file=>({file,preview:URL.createObjectURL(file),mime_type:file.type}))
      ].slice(0,2));
    }catch(error){setPhotoError(String(error?.message||error))}
  };
  const removeExtra=index=>setExtraPhotos(current=>{
    const removed=current[index];
    if(removed?.preview?.startsWith('blob:'))URL.revokeObjectURL(removed.preview);
    return current.filter((_,i)=>i!==index);
  });
  const identify=async()=>{
    const images=[
      ...(photoFile?[{file:photoFile}]:form.image_path?[{path:form.image_path}]:[]),
      ...extraPhotos.map(item=>item.file?{file:item.file}:{path:item.path})
    ].filter(x=>x.path||x.file).slice(0,3);
    if(!images.length){setAiError('Adicione uma foto do produto antes de identificar com IA.');return}
    setAiError('');setAiResult(null);setAiBusy(true);
    try{
      const result=await onIdentify(images);
      if(!result?.suggestion)throw new Error('A identificação não retornou dados.');
      setAiResult(result);
    }catch(error){setAiError(String(error?.message||error))}
    finally{setAiBusy(false)}
  };
  const accept=()=>{
    if(!aiResult?.suggestion)return;
    const suggestion=aiResult.suggestion;
    const classification=proposeClassification(suggestion,form.name||suggestion.name);
    setForm(current=>{
      const next=applyVisualProductSuggestion(current,{...suggestion,category:classification.category},categories);
      return {...next,metadata:{...(next.metadata||{}),ai_review:{
        source:'smartpdv-product-enrich',engine:aiResult.engine,
        confidence:suggestion.confidence,
        accepted_at:new Date().toISOString(),
        visual_facts:suggestion.visual_facts||[],
        questions:suggestion.questions||[]
      },classification:{
        ...(next.metadata?.classification||{}),
        category_suggestion:classification.category,
        subcategory_suggestion:classification.subcategory,
        source:classification.basis
      }}};
    });
    setAiResult(null);
  };

  return <section className="catalogIntelligence">
    {mode==='media' && <>
    <div className="galleryHeading"><strong>Galeria do produto · {Math.min(3,1+extraPhotos.length)}/3</strong>
      <small>Foto principal acima. Adicione o verso e detalhes para o catálogo físico e online.</small>
    </div>
    <div className="galleryTiles">
      {extraPhotos.map((photo,i)=><div className="galleryTile" key={photo.path||photo.preview}>
        {photo.preview?<img src={photo.preview} alt={'Foto adicional '+(i+2)}/>:<ImagePlus size={28} aria-label={'Foto adicional '+(i+2)}/>}
        <button type="button" aria-label={'Remover foto '+(i+2)} onClick={()=>removeExtra(i)}><X size={15}/></button>
      </div>)}
      {extraPhotos.length<2&&<div className="galleryAdd">
        <label className="cameraButton"><ImagePlus size={16}/> Escolher imagem
          <input type="file" multiple hidden accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            onChange={e=>{addPhotos(e.target.files);e.currentTarget.value=''}}/>
        </label>
        <label className="cameraButton"><Camera size={16}/> Fotografar detalhe
          <input type="file" hidden accept="image/*" capture="environment"
            onChange={e=>{addPhotos(e.target.files);e.currentTarget.value=''}}/>
        </label>
      </div>}
    </div>
    {photoError&&<small className="photoError" role="alert">{photoError}</small>}

    {(barcodeBusy||barcodeNotice||detectedMatches.length>0)&&
      <div className="productMatchAssist" aria-live="polite">
        {barcodeBusy&&<small>Verificando automaticamente o código das fotos…</small>}
        {barcodeNotice&&<small>{barcodeNotice}</small>}
        {detectedMatches.length>0&&<div className="masterMatchedProducts" role="alert">
          <strong>Possível produto já cadastrado — confira antes de criar outro</strong>
          {detectedMatches.map(({product,reason})=><button key={product.id}
            type="button" onClick={()=>{setAiResult(null);onOpenExisting?.(product)}}>
            {product.name} · {product.barcode||product.sku}
            <span>{reason==='barcode'?'Mesmo GTIN':reason==='sku'?'Mesmo SKU':'Mesmo nome e marca'} · Abrir</span>
          </button>)}
        </div>}
      </div>}

    <div className="aiCatalogActions">
      <button type="button" className="cameraButton" disabled={aiBusy||!onIdentify} onClick={identify}>
        {aiBusy?<Loader2 className="spin" size={17}/>:<Sparkles size={17}/>}
        {aiBusy?'Reconhecendo…':'Identificar produto com IA'}
      </button>
      <small>Reconhecimento por IA com revisão humana. O código e os produtos existentes são verificados automaticamente; confirme os dados antes de aplicar.</small>
    </div>
    {aiError&&<small className="photoError" role="alert">{aiError}</small>}
    {aiResult&&<div className="aiCatalogProposal">
      <strong><Sparkles size={15}/> Sugestão · confiança {aiResult.suggestion.confidence||0}%</strong>
      <p><b>{aiResult.suggestion.name||'Identidade incerta'}</b> · {aiResult.suggestion.brand||'Marca não identificada'}</p>
      <p>{aiResult.suggestion.description||'Sem descrição confirmada.'}</p>
      <small>Categoria: {proposeClassification(aiResult.suggestion).category||'Sem evidência'} · Subcategoria: {proposeClassification(aiResult.suggestion).subcategory||'Não sugerida'} · Código visto: {aiResult.suggestion.barcode_visible||'Não confirmado'}</small>
      {aiResult.suggestion.questions?.length>0&&<ul>{aiResult.suggestion.questions.map((q,i)=><li key={i}>{q}</li>)}</ul>}
      {aiResult.suggestion.notes&&<small>{aiResult.suggestion.notes}</small>}
      <button type="button" className="cameraButton" onClick={accept}><Check size={16}/> Aplicar campos vazios</button>
    </div>}
    </>}
    {mode==='commerce' && <>
    <div className="commerceHeading"><strong>Informações para vender localmente e online</strong>
      <small>Pesquisável no catálogo, pronto para integrações futuras. Nenhum anúncio é publicado automaticamente.</small>
    </div>
    <div className="commerceGrid">
      <CommerceInput label="Título da vitrine online" value={commerce.online_title} onChange={v=>setCommerce('online_title',v)}/>
      <CommerceInput label="Modelo ou variante" value={commerce.model_variant} onChange={v=>setCommerce('model_variant',v)}/>
      <CommerceInput label="Tamanho / apresentação" value={commerce.package_size} onChange={v=>setCommerce('package_size',v)}/>
      <CommerceInput label="Palavras de busca" value={Array.isArray(commerce.search_keywords)?commerce.search_keywords.join(', '):''}
        hint="Separe por vírgulas."
        onChange={v=>setCommerce('search_keywords',v.split(',').map(x=>x.trim()).filter(Boolean))}/>
      <div className="wide"><CommerceInput multiline label="Descrição para comércio online" value={commerce.online_description} onChange={v=>setCommerce('online_description',v)}/></div>
      <div className="wide"><CommerceInput multiline label="Aplicação e contexto local comprovados" value={commerce.local_usage} onChange={v=>setCommerce('local_usage',v)}/></div>
      <CommerceInput label="Peso embalado para envio (g)" value={commerce.shipping_weight_g} onChange={v=>setCommerce('shipping_weight_g',v.replace(/[^0-9]/g,''))}/>
      <CommerceInput label="Dimensões para envio (C × L × A, cm)" value={commerce.shipping_dimensions_cm} onChange={v=>setCommerce('shipping_dimensions_cm',v)}/>
      <CommerceInput label="Material / composição confirmada" value={commerce.material_composition} onChange={v=>setCommerce('material_composition',v)}/>
      <CommerceInput label="Garantia ou instruções do fabricante" value={commerce.warranty_notes} onChange={v=>setCommerce('warranty_notes',v)}/>
      <div className="wide"><CommerceInput multiline label="Observações internas (não exibir online)" value={commerce.internal_notes} onChange={v=>setCommerce('internal_notes',v)}/></div>
    </div>
    </>}
  </section>;
}
